import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import CommonImg from "@/components/Common/Img";

import ArrowLineLeftPng from "@/assets/images/arrow-line-left.png";
import ArrowLineRightPng from "@/assets/images/arrow-line-right.png";

import { useEffect, useState } from "react";
import { cn } from "@/libs/utils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";

interface DataTableProps<
  TData extends { onRowClick?: () => void; disabled?: boolean },
  TValue,
> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isPagination?: boolean;
  filterComponent?: React.ReactNode;
  containerClassName?: string;
  pageSize?: number;
  onRowSelect?: (row: TData | null) => void;
  tableHeadClassName?: string;
  tableCellClassName?: string;
}

export default function DataTable<TData, TValue>({
  columns,
  data,
  isPagination = true,
  filterComponent,
  containerClassName,
  pageSize = 20,
  onRowSelect,
  tableHeadClassName,
  tableCellClassName,
}: DataTableProps<
  TData & { onRowClick?: () => void; disabled?: boolean },
  TValue
>) {
  const [open, setOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: pageSize,
      },
    },
  });

  useEffect(() => {
    const selected = table.getSelectedRowModel().rows?.[0]?.original ?? null;
    onRowSelect?.(selected);
  }, [table.getSelectedRowModel().rows, onRowSelect]);

  const renderPageNumbers = () => {
    const pageCount = table.getPageCount();
    const pageIndex = table.getState().pagination.pageIndex;
    const startPageIndex = Math.max(
      0,
      Math.min(pageIndex - Math.floor(5 / 2), pageCount - 5)
    );
    return Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
      const pageNumber = startPageIndex + i;
      return (
        <Button
          key={`pagination-${pageNumber}`}
          variant="ghost"
          size="sm"
          className={`p-1 w-7 h-7 text-gray-A text-sm font-normal ${pageNumber === pageIndex ? "bg-gray-E rounded-md" : ""}`}
          onClick={() => table.setPageIndex(pageNumber)}
        >
          {pageNumber + 1}
        </Button>
      );
    });
  };

  return (
    <div className={cn("flex flex-col w-full h-full", containerClassName)}>
      {filterComponent && <>{filterComponent}</>}
      {/* 컬럼 size 합이 컨테이너 폭을 넘을 때 페이지가 아닌 테이블 내부에서 가로 스크롤 */}
      <div className="w-full overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.size,
                        maxWidth: header.column.columnDef.size,
                      }}
                      className={tableHeadClassName}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn(
                    typeof row.original.onRowClick === "function" &&
                      "cursor-pointer",
                    row.original.disabled && "opacity-50 bg-light-gray"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        minWidth: cell.column.columnDef.size,
                        maxWidth: cell.column.columnDef.size,
                      }}
                      onClick={(e) => {
                        const isInteractive =
                          cell.column.columnDef.meta &&
                          "isInteractive" in cell.column.columnDef.meta &&
                          cell.column.columnDef.meta.isInteractive;

                        if (row.original.disabled) {
                          e.stopPropagation();
                          e.preventDefault();
                          setOpen(true);
                          return;
                        }

                        if (
                          typeof row.original.onRowClick === "function" &&
                          !isInteractive
                        ) {
                          row.original.onRowClick();
                        }
                      }}
                      className={tableCellClassName}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {isPagination && (
        <div className="flex items-center justify-center space-x-2 mt-3 mb-5">
          <Button
            variant="ghost"
            size="sm"
            className="p-1 w-7 h-7"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <CommonImg
              src={ArrowLineLeftPng}
              alt="Chevron"
              className="size-[20px]"
            />
          </Button>
          {renderPageNumbers()}
          <Button
            variant="ghost"
            size="sm"
            className="p-1 w-7 h-7"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <CommonImg
              src={ArrowLineRightPng}
              alt="Chevron"
              className="size-[20px]"
            />
          </Button>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="h-[222px]">
          <DialogHeader>
            <DialogTitle>권한 없음</DialogTitle>
            <DialogDescription />
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <p className="text-[#000000] text-lg font-normal text-center">
              프로젝트 관리자에게 초대를 요청해주세요
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
